/**
 * Which rows to render.
 *
 * A pure function of scroll position and geometry: no React, no DOM. That is
 * what makes it testable at the sizes that matter - forty thousand rows, a
 * viewport part-way down, the very end of the list - where a component test in
 * a layout-less environment can prove nothing, because every measurement there
 * is zero.
 */

export interface WindowGeometry {
  /** Distance the scroll container has been scrolled. */
  scrollTop: number;
  /** Visible height of the scroll container. Zero when it cannot be measured. */
  viewportHeight: number;
  rowHeight: number;
  rowCount: number;
  /** Rows rendered beyond each edge, so scrolling does not reveal blank space. */
  overscan?: number;
}

export interface WindowRange {
  start: number;
  /** Exclusive. */
  end: number;
  /** Height of the rows above the window, used to offset the rendered block. */
  paddingTop: number;
  /** Height of the rows below it. */
  paddingBottom: number;
  /** True when every row is rendered, because the geometry was not measurable. */
  renderedAll: boolean;
}

export const DEFAULT_OVERSCAN = 8;

export function visibleRange(geometry: WindowGeometry): WindowRange {
  const { scrollTop, viewportHeight, rowHeight, rowCount } = geometry;
  const overscan = geometry.overscan ?? DEFAULT_OVERSCAN;

  // An unmeasurable viewport means rendering everything, and saying so. The
  // alternative - guessing a height - renders a window that does not match
  // what anyone can see, and it is indistinguishable from a bug.
  if (viewportHeight <= 0 || rowHeight <= 0) {
    return { start: 0, end: rowCount, paddingTop: 0, paddingBottom: 0, renderedAll: true };
  }

  const first = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const visible = Math.ceil(viewportHeight / rowHeight);
  const last = Math.min(rowCount, first + visible + overscan * 2);

  return {
    start: first,
    end: last,
    paddingTop: first * rowHeight,
    paddingBottom: Math.max(0, (rowCount - last) * rowHeight),
    renderedAll: first === 0 && last === rowCount,
  };
}

/** Total scrollable height, so the scrollbar reflects the whole list. */
export function totalHeight(rowCount: number, rowHeight: number): number {
  return Math.max(0, rowCount * rowHeight);
}
